// CalendarSetupPage.jsx
import React, { useState, useEffect } from 'react';
import { 
    Box, Button, Typography, List, ListItem, ListItemText, 
    ListItemSecondaryAction, IconButton, Chip, Paper,
    Dialog, DialogTitle, DialogContent, DialogActions,
    Snackbar, Alert
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import RefreshIcon from '@mui/icons-material/Refresh';
import { doc, getDoc, setDoc, arrayUnion } from 'firebase/firestore';
import { db } from '../../../lib/firebase'; // Adjust path if needed

const SERVICE_ACCOUNT_EMAIL = "396023155854-compute@developer.gserviceaccount.com";
const CLIENT_ID = import.meta.env.VITE_GOOGLE_CALENDAR_CLIENT_ID;
const SCOPES = 'https://www.googleapis.com/auth/calendar'; // Full access needed to modify ACLs

const CalendarSetupPage = () => {
    const [tokenClient, setTokenClient] = useState(null);
    const [accessToken, setAccessToken] = useState(null);
    const [calendars, setCalendars] = useState([]);
    const [connectedCalendars, setConnectedCalendars] = useState(new Set());
    const [processing, setProcessing] = useState(null); // ID of calendar being processed
    
    // Dialog State
    const [openDialog, setOpenDialog] = useState(false);
    const [copySuccess, setCopySuccess] = useState(false);
    const [errorDialogOpen, setErrorDialogOpen] = useState(false);
    const [errorMessage, setErrorMessage] = useState("");
    
    // ACL Status State
    const [calendarAclStatus, setCalendarAclStatus] = useState({}); // { [id]: 'verified' | 'missing' | 'unknown' }
    const [verifyingAcl, setVerifyingAcl] = useState(false);

    // 1. Load Google Identity Services Script
    useEffect(() => {
        const script = document.createElement('script');
        script.src = 'https://accounts.google.com/gsi/client';
        script.async = true;
        script.defer = true;
        script.onload = () => {
             const client = window.google.accounts.oauth2.initTokenClient({
                client_id: CLIENT_ID,
                scope: SCOPES,
                callback: (tokenResponse) => {
                    if (tokenResponse && tokenResponse.access_token) {
                        setAccessToken(tokenResponse.access_token);
                    }
                },
            });
            setTokenClient(client);
        };
        document.body.appendChild(script);
        
        // Load existing connected calendars from Firestore
        const loadFirestoreConfig = async () => {
            const docRef = doc(db, 'settings', 'calendar');
            const docSnap = await getDoc(docRef);
            if (docSnap.exists()) {
                const data = docSnap.data();
                const ids = new Set((data.calendars || []).map(c => c.id));
                setConnectedCalendars(ids);
            }
        };
        loadFirestoreConfig();
        
        return () => { try { document.body.removeChild(script); } catch(e){} }
    }, []);

    // 2. Fetch User's Calendars once logged in
    useEffect(() => {
        if (!accessToken) return;

        fetch('https://www.googleapis.com/calendar/v3/users/me/calendarList', {
            headers: { 'Authorization': `Bearer ${accessToken}` }
        })
        .then(res => res.json())
        .then(data => {
            setCalendars(data.items || []);
        });
    }, [accessToken]);

    // 3. Verify ACL for Connected Calendars
    const checkCalendarPermissions = async () => {
        if (!accessToken || connectedCalendars.size === 0) return;
        
        setVerifyingAcl(true);
        const newStatus = { ...calendarAclStatus };

        for (const calId of connectedCalendars) {
            try {
                const res = await fetch(`https://www.googleapis.com/calendar/v3/calendars/${encodeURIComponent(calId)}/acl`, {
                    headers: { 'Authorization': `Bearer ${accessToken}` }
                });
                
                if (!res.ok) {
                    // If we can't even read the ACL, we definitely don't have Full Access, 
                    // but we might still have Reader access if the calendar is public or shared.
                    // However, we are checking if *Service Account* has access.
                    // If WE can't see the ACL, we can't verify. Assume missing/unknown.
                    newStatus[calId] = 'unknown'; 
                    continue;
                }

                const data = await res.json();
                const hasAccess = (data.items || []).some(
                    rule => rule.scope.type === 'user' && rule.scope.value === SERVICE_ACCOUNT_EMAIL
                );
                
                newStatus[calId] = hasAccess ? 'verified' : 'missing';
                
            } catch (error) {
                console.error(`Error checking ACL for ${calId}:`, error);
                newStatus[calId] = 'unknown';
            }
        }
        
        setCalendarAclStatus(newStatus);
        setVerifyingAcl(false);
    };

    useEffect(() => {
        checkCalendarPermissions();
    }, [connectedCalendars, accessToken]);


    const handleLogin = () => {
        tokenClient.requestAccessToken();
    };


    const handleConnect = async (calendar) => {
        setProcessing(calendar.id);
        let shareSuccess = false;

        try {
            // A. Share with Service Account (Modify ACL)
            const aclRes = await fetch(`https://www.googleapis.com/calendar/v3/calendars/${encodeURIComponent(calendar.id)}/acl`, {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${accessToken}`,
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({
                    role: 'reader',
                    scope: {
                        type: 'user',
                        value: SERVICE_ACCOUNT_EMAIL
                    }
                })
            });

            if (aclRes.ok || aclRes.status === 409) {
                shareSuccess = true;
            } else {
                console.warn("Auto-share failed:", await aclRes.text());
                // Don't return, just mark as failed so we can warn user
            }

            // B. Add to Firestore (ALWAYS do this, even if share fails)
            const calConfig = {
                id: calendar.id,
                summary: calendar.summary,
                backgroundColor: calendar.backgroundColor || '#4fd1c5'
            };

            await setDoc(doc(db, 'settings', 'calendar'), {
                calendars: arrayUnion(calConfig)
            }, { merge: true });

            setConnectedCalendars(prev => new Set(prev).add(calendar.id));

            if (!shareSuccess) {
                // Open the Help Dialog
                setOpenDialog(true);
            }

        } catch (err) {
            console.error(err);
            setErrorMessage(err.message || "Unknown error occurred.");
            setErrorDialogOpen(true);
        } finally {
            setProcessing(null);
        }
    };

    const handleCopyEmail = () => {
        navigator.clipboard.writeText(SERVICE_ACCOUNT_EMAIL);
        setCopySuccess(true);
    };

    const handleCloseDialog = () => {
        setOpenDialog(false);
    };


    return (
        <Box sx={{ 
            minHeight: '100vh', 
            bgcolor: '#f7fafc', 
            p: 4, 
            display: 'flex', 
            justifyContent: 'center' 
        }}>
            <Paper sx={{ maxWidth: 600, width: '100%', p: 4, borderRadius: 4 }}>
                <Typography variant="h4" gutterBottom fontWeight="bold">
                    Kiosk Calendar Setup
                </Typography>
                <Typography variant="body1" color="text.secondary" paragraph>
                    Log in with your family account to choose which calendars appear on the kitchen display.
                </Typography>

                {!accessToken ? (
                    <Button 
                        variant="contained" 
                        size="large" 
                        onClick={handleLogin}
                        sx={{ mt: 2, bgcolor: '#4285f4', '&:hover': { bgcolor: '#3367d6'} }}
                    >
                        Sign in with Google
                    </Button>
                ) : (
                    <List sx={{ mt: 2 }}>
                        {calendars.map(cal => {
                            const isConnected = connectedCalendars.has(cal.id);
                            
                             return (
                                <ListItem key={cal.id} divider>
                                    <Box sx={{ 
                                        width: 16, 
                                        height: 16, 
                                        borderRadius: '50%', 
                                        bgcolor: cal.backgroundColor || '#ccc',
                                        mr: 2
                                    }} />
                                    <ListItemText 
                                        primary={cal.summary} 
                                        secondary={isConnected ? (
                                            calendarAclStatus[cal.id] === 'missing' ? "Action Required: Share with robot" :
                                            calendarAclStatus[cal.id] === 'verified' ? "Connected & Verified" :
                                            "Connected"
                                        ) : "Not visible"}
                                    />
                                    <ListItemSecondaryAction>
                                        {isConnected ? (
                                            calendarAclStatus[cal.id] === 'missing' ? (
                                                 <Chip 
                                                    icon={<WarningAmberIcon />} 
                                                    label="Needs Sharing" 
                                                    color="warning" 
                                                    onClick={() => setOpenDialog(true)}
                                                />
                                            ) : (
                                                <Chip 
                                                    icon={<CheckCircleIcon />} 
                                                    label="Active" 
                                                    color="success" 
                                                    variant="outlined" 
                                                />
                                            )
                                        ) : (
                                            <Button
                                                variant="contained"
                                                size="small"
                                                disabled={processing === cal.id}
                                                onClick={() => handleConnect(cal)}
                                                startIcon={<AddCircleOutlineIcon />}
                                            >
                                                {processing === cal.id ? "..." : "Add"}
                                            </Button>
                                        )}
                                    </ListItemSecondaryAction>
                                </ListItem>
                            );
                        })}
                    </List>
                )}
                
                {connectedCalendars.size > 0 && accessToken && (
                    <Box sx={{ mt: 3, display: 'flex', justifyContent: 'center' }}>
                        <Button 
                            startIcon={<RefreshIcon />} 
                            size="small" 
                            disabled={verifyingAcl}
                            onClick={checkCalendarPermissions}
                        >
                            {verifyingAcl ? "Verifying Access..." : "Check Connections"}
                        </Button>
                    </Box>
                )}
            </Paper>

            {/* Manual Share Instruction Dialog */}
            <Dialog open={openDialog} onClose={handleCloseDialog}>
                <DialogTitle>Calendar Added!</DialogTitle>
                <DialogContent>
                    <Typography paragraph>
                        We've added this calendar to the Kiosk database. However, we couldn't automatically update the sharing settings.
                    </Typography>
                    <Typography paragraph>
                        Please manually share this calendar with the Kiosk Robot email:
                    </Typography>
                    
                    <Box sx={{ 
                        p: 2, 
                        bgcolor: '#edf2f7', 
                        borderRadius: 2, 
                        display: 'flex', 
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        wordBreak: 'break-all'
                    }}>
                        <Typography variant="body2" component="code" sx={{ fontFamily: 'monospace', mr: 1 }}>
                            {SERVICE_ACCOUNT_EMAIL}
                        </Typography>
                        <IconButton onClick={handleCopyEmail} color="primary">
                            <ContentCopyIcon />
                        </IconButton>
                    </Box>
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleCloseDialog}>Done</Button>
                </DialogActions>
            </Dialog>
            <Snackbar
                open={copySuccess}
                autoHideDuration={2000}
                onClose={() => setCopySuccess(false)}
            >
                <Alert onClose={() => setCopySuccess(false)} severity="success" sx={{ width: '100%' }}>
                    Email copied to clipboard!
                </Alert>
            </Snackbar>

            {/* Error Dialog */}
            <Dialog 
                open={errorDialogOpen} 
                onClose={() => setErrorDialogOpen(false)}
                aria-labelledby="error-dialog-title"
                aria-describedby="error-dialog-description"
            >
                <DialogTitle id="error-dialog-title" color="error">
                    Connection Error
                </DialogTitle>
                <DialogContent>
                    <Typography id="error-dialog-description">
                        We encountered an issue adding this calendar.
                    </Typography>
                    <Box sx={{ mt: 2, p: 2, bgcolor: '#fff5f5', borderRadius: 1, border: '1px solid #fed7d7' }}>
                        <Typography variant="caption" fontFamily="monospace" color="error.main">
                            {errorMessage}
                        </Typography>
                    </Box>
                    {errorMessage.includes("Missing or insufficient permissions") && (
                        <Typography variant="body2" sx={{ mt: 2 }} color="text.secondary">
                            <strong>Tip:</strong> This error usually means you don't have permission to modify database settings. Ensure you are logged into the main application.
                        </Typography>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setErrorDialogOpen(false)} color="primary" autoFocus>
                        Close
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default CalendarSetupPage;
